import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { AuthProvider } from './store/AuthProvider'
import { ToastProvider } from './store/ToastProvider'
import { ThemeProvider } from './store/ThemeProvider'
import { isConfigured } from './services/firebase'
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: "https://4da580819cbdfe02ef081f289745a2fa@o4511869954621440.ingest.de.sentry.io/4511869960978512",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD,
});

// Disable pinch zoom and multi-touch zoom gestures for native app feel
document.addEventListener('gesturestart', (e) => { e.preventDefault(); });
document.addEventListener('gesturechange', (e) => { e.preventDefault(); });
document.addEventListener('touchstart', (e) => {
  if (e.touches.length > 1) {
    e.preventDefault();
  }
}, { passive: false });

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');
const root = createRoot(rootElement);

if (!isConfigured) {
  root.render(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-red-50 p-4">
      <div className="max-w-md space-y-4 rounded-xl bg-white p-6 shadow-xl border border-red-200 font-sans">
        <h2 className="text-xl font-bold text-red-600">Application Configuration Error</h2>
        <p className="text-sm text-slate-600">
          The application refused to start because it is missing valid Firebase credentials.
        </p>
        <div className="rounded bg-slate-100 p-3 text-xs font-mono text-slate-700">
          <p>Environment: {import.meta.env.MODE}</p>
          <p>API Key Configured: No</p>
        </div>
        <p className="text-xs font-bold text-slate-800">
          To fix this issue:
        </p>
        <ul className="list-disc pl-5 text-xs text-slate-600 space-y-1">
          <li>Check your <code>.env</code> file (local) or Vercel/Netlify settings (production).</li>
          <li>Ensure <code>VITE_FIREBASE_API_KEY</code> is set to a valid Firebase API Key.</li>
        </ul>
      </div>
    </div>
  )
} else {
  root.render(
    <StrictMode>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <Sentry.ErrorBoundary fallback={<div className="flex h-screen items-center justify-center p-4 text-center"><p>An error has occurred. Our team has been notified.</p></div>}>
              <App />
            </Sentry.ErrorBoundary>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </StrictMode>,
  )
}
